import PropTypes from 'prop-types';
import useSearchQuery from 'queries/useSearchQuery';
import { useStateContext } from '@activebrands/core-web/contexts/StateContext';
import { useWillUnmount } from '@activebrands/core-web/hooks/lifecycle';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import overlay, { useOverlaySubscriber } from '@activebrands/core-web/libs/overlay';
import { styled } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import { updateQueryParams } from '@activebrands/core-web/utils/query';
import debounce from '@grebban/utils/function/debounce';
import extractQueryParams from '@grebban/utils/string/extractQueryParams';
import { useLocation } from '@reach/router';
import SearchIcon from 'components/icons/SearchIcon';
import Input from 'components/inputs/Input';
import fm from 'format-message';

const Form = styled('form', {
    width: '219px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0px 8px',
    margin: '0 8px',
    color: 'inherit',
    cursor: 'text',
    border: '1px solid var(--color-border-search-header)',
    borderRadius: '50px',
    backgroundColor: 'var(--color-bg-search-header)',
    transitionDelay: 'var(--header-transition-delay)',
    transitionTimingFunction: 'var(--ease)',
    transitionDuration: 'var(--duration-fast)',
    transitionProperty: 'border-color, background-color, color',

    ...getFocusRingStyles(':focus'),
    ...getFocusRingStyles(':has(input:focus-visible)'),
});

const SearchInput = ({ previewDataOverride, skipLinkTargetId, ...rest }) => {
    const [state, setState] = useStateContext();
    const location = useLocation();
    const [isSearchOpen] = useOverlaySubscriber([OVERLAY_ID.SEARCH]);

    const { headerConfigContent, searchPage } = useSearchQuery();
    const { searchInput } = previewDataOverride || headerConfigContent;

    const handleInputChange = debounce(({ target }) => {
        const query = target.value;
        setState(query);
    }, 500);

    // Navigate to search page and trigger a search there
    const onSubmit = e => {
        e.preventDefault();
        overlay.close(OVERLAY_ID.SEARCH);
        handleInputChange.cancel();

        let params = {
            q: e.target.elements[0].value,
        };

        // Keep all params except page if the search is performed on the searchpage
        if (location.pathname === searchPage) {
            const queryParams = extractQueryParams(location.search);
            delete queryParams.page;
            params = { ...queryParams, ...params };
        }

        // Update the http-query, this will trigger a search on the search page
        updateQueryParams(params, { basePath: searchPage });
    };

    const setFocus = ({ target }) => {
        let inputEle;
        let openedSearchOverlay = false;

        switch (target.tagName) {
            case 'INPUT':
                inputEle = target;
                overlay.closeAll();
                overlay.open(OVERLAY_ID.SEARCH, { previewDataOverride });
                openedSearchOverlay = true;
                break;

            case 'FORM':
                inputEle = target.getElementsByTagName('input')[0];
                break;

            default:
                inputEle = target.closest('form')?.getElementsByTagName('input')[0];
                break;
        }

        if (!inputEle) {
            return;
        }

        inputEle.focus();
        if (openedSearchOverlay) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    inputEle.focus();
                });
            });
        }
    };

    useWillUnmount(() => handleInputChange.cancel());

    return (
        <Form
            data-selector="global.nav.search"
            role="search"
            {...rest}
            id={skipLinkTargetId}
            tabIndex={skipLinkTargetId ? -1 : undefined}
            $isActive={isSearchOpen}
            onClick={setFocus}
            onSubmit={onSubmit}
        >
            <SearchIcon width="14px" />
            <Input
                $style={{ marginTop: '0' }}
                ariaLabel={fm('Search')}
                autoComplete="off"
                inputStyle={{
                    width: '100%',
                    background: 'none',
                    height: 'auto',
                    padding: '2px',
                    border: 'none',
                    color: 'inherit',
                    outline: 'none',
                    ':focus-visible': { outline: 'none' },
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '::placeholder': {
                        color: 'var(--color-text-input)',
                    },
                }}
                inputFontKeys="Secondary/14_130"
                maxLength="100"
                name="header-search"
                placeholder={searchInput}
                value={state}
                onChange={handleInputChange}
            />
        </Form>
    );
};

SearchInput.propTypes = {
    previewDataOverride: PropTypes.object,
    skipLinkTargetId: PropTypes.string,
};

export default SearchInput;
